import { useCallback, useEffect, useRef, useState } from 'react';
import { MicIcon, PauseIcon, PlayIcon, SquareIcon } from 'lucide-react';

import { Alert, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import { Spinner } from '@/components/ui/spinner';
import { Niveaumeter } from './Niveaumeter';
import { herstelOpname, kanOpnemen, startOpname, tijdTekst, type OpnameSessie } from './opname';
import { vergeetOpname } from './opnameOpslag';
import { houdSchermWakker } from './wakker';

const omvang = (bytes: number) =>
  bytes > 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`
    : `${Math.round(bytes / 1024)} kB`;

/**
 * De eerste stap van een opgenomen gesprek: de microfoon zelf.
 *
 * Een eigen component omdat hier het enige zit wat met apparaten te maken heeft
 * — toestemming, formaat, het scherm wakker houden — terwijl het venster
 * eromheen alleen stappen telt. Wat eruit komt is een `File` en daarmee is het
 * verschil met een gesleept bestand op.
 */
export function OpnamePaneel({
  bestand,
  onBestand,
  onLoopt,
}: {
  bestand: File | null;
  onBestand: (bestand: File | null) => void;
  /** Zolang dit waar is gaat het venster niet dicht en blijft het scherm aan. */
  onLoopt: (loopt: boolean) => void;
}) {
  const [sessie, setSessie] = useState<OpnameSessie | null>(null);
  const [gepauzeerd, setGepauzeerd] = useState(false);
  const [seconden, setSeconden] = useState(0);
  const [niveau, setNiveau] = useState(0);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState('');
  const [teruggevonden, setTeruggevonden] = useState<{ id: string; bestand: File } | null>(null);

  const laatWakker = useRef<(() => void) | null>(null);
  /** De lopende sessie, ook buiten de render om. Nodig bij het opruimen: een
   *  effect dat op `sessie` wacht ziet de laatste waarde niet meer als de
   *  component al weg is. */
  const lopende = useRef<OpnameSessie | null>(null);
  const startBezig = useRef(false);
  const loopt = sessie !== null;

  /** Een opname die is blijven liggen na een crash. */
  useEffect(() => {
    void herstelOpname().then((gevonden) => {
      if (gevonden) setTeruggevonden(gevonden);
    });
  }, []);

  useEffect(() => {
    onLoopt(loopt);
  }, [loopt, onLoopt]);

  /** Verdwijnt dit scherm terwijl er nog wordt opgenomen, dan gaat de microfoon
   *  uit. Zonder dit blijft de stream open zolang het tabblad leeft, met het
   *  opnamelampje aan en zonder dat er iets bewaard wordt. */
  useEffect(
    () => () => {
      void lopende.current?.afbreken();
      lopende.current = null;
    },
    [],
  );

  // Zolang er wordt opgenomen mag het scherm niet uitvallen en het tabblad niet
  // zomaar dicht.
  useEffect(() => {
    if (!loopt) return;
    void houdSchermWakker().then((laatLos) => {
      laatWakker.current = laatLos;
    });
    const waarschuw = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', waarschuw);
    return () => {
      window.removeEventListener('beforeunload', waarschuw);
      laatWakker.current?.();
      laatWakker.current = null;
    };
  }, [loopt]);

  const begin = useCallback(async () => {
    // Twee keer klikken op de knop opende twee keer de microfoon. De tweede
    // stream kwam in de staat te staan, de eerste bleef open — en dan blijft het
    // lampje van de browser branden nadat je op stoppen hebt gedrukt. `bezig` in
    // de staat is hier te traag voor: die is pas na de render zichtbaar.
    if (startBezig.current) return;
    startBezig.current = true;
    setFout('');
    setBezig(true);
    try {
      const nieuw = await startOpname({
        onTijd: setSeconden,
        onNiveau: setNiveau,
        onOnderbroken: setFout,
      });
      setTeruggevonden(null);
      lopende.current = nieuw;
      setSessie(nieuw);
      setGepauzeerd(false);
      setSeconden(0);
    } catch (err) {
      setFout(
        err instanceof DOMException &&
          (err.name === 'NotAllowedError' || err.name === 'SecurityError')
          ? 'Geen toegang tot de microfoon.'
          : err instanceof Error
            ? err.message
            : String(err),
      );
    } finally {
      startBezig.current = false;
      setBezig(false);
    }
  }, []);

  async function stop() {
    if (!sessie) return;
    setBezig(true);
    try {
      onBestand(await sessie.stop());
      lopende.current = null;
      setSessie(null);
      setNiveau(0);
    } catch (err) {
      // Ook bij een mislukte stop de microfoon dicht: anders staat er een fout
      // op het scherm terwijl het lampje blijft branden.
      void sessie.afbreken();
      lopende.current = null;
      setSessie(null);
      setFout(err instanceof Error ? err.message : String(err));
    } finally {
      setBezig(false);
    }
  }

  if (bestand) {
    return (
      <Item variant="outline">
        <ItemMedia>
          <span className="bg-primary/10 text-primary flex size-10 items-center justify-center rounded-lg">
            <MicIcon className="size-5" />
          </span>
        </ItemMedia>
        <ItemContent className="min-w-0">
          <ItemTitle>Opname klaar</ItemTitle>
          <ItemDescription>
            {tijdTekst(seconden)} · {omvang(bestand.size)}
          </ItemDescription>
        </ItemContent>
        <ItemActions>
          <Button
            disabled={bezig}
            onClick={() => {
              onBestand(null);
              setSeconden(0);
              void begin();
            }}
            size="sm"
            variant="ghost"
          >
            Opnieuw opnemen
          </Button>
        </ItemActions>
      </Item>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {fout && (
        <Alert variant="destructive">
          <AlertTitle>{fout}</AlertTitle>
        </Alert>
      )}

      {teruggevonden && !loopt && (
        <Item variant="outline">
          <ItemContent className="min-w-0">
            <ItemTitle>Er staat nog een opname klaar</ItemTitle>
            <ItemDescription>
              {teruggevonden.bestand.name} · {omvang(teruggevonden.bestand.size)}
            </ItemDescription>
          </ItemContent>
          <ItemActions>
            <Button
              onClick={() => {
                void vergeetOpname(teruggevonden.id);
                setTeruggevonden(null);
              }}
              size="sm"
              variant="ghost"
            >
              Weggooien
            </Button>
            <Button
              onClick={() => {
                onBestand(teruggevonden.bestand);
                setTeruggevonden(null);
              }}
              size="sm"
            >
              Gebruiken
            </Button>
          </ItemActions>
        </Item>
      )}

      {/* Eén vak, in beide standen even hoog: de knop staat waar straks de tijd
          komt, dus er springt niets als je begint. Een doorgetrokken lijn, geen
          stippellijn: die laatste belooft dat je er iets in kunt slepen. */}
      <div className="bg-muted/40 flex min-h-32 flex-col items-center justify-center gap-4 rounded-xl border p-6">
        {loopt ? (
          <>
            <div aria-live="polite" className="flex items-center gap-2.5" role="status">
              <span
                className={
                  gepauzeerd
                    ? 'bg-muted-foreground size-2 rounded-full'
                    : 'bg-destructive ring-destructive/15 size-2 animate-pulse rounded-full ring-4'
                }
              />
              <span
                className={
                  gepauzeerd
                    ? 'text-muted-foreground text-xs font-medium tracking-wide uppercase'
                    : 'text-destructive text-xs font-medium tracking-wide uppercase'
                }
              >
                {gepauzeerd ? 'Gepauzeerd' : 'Opname loopt'}
              </span>
            </div>

            <span className="text-5xl leading-none font-light tabular-nums">
              {tijdTekst(seconden)}
            </span>

            <Niveaumeter niveau={gepauzeerd ? 0 : niveau} />

            <div className="flex items-center gap-2">
              <Button disabled={bezig} onClick={() => void stop()} size="sm">
                {bezig ? (
                  <Spinner data-icon="inline-start" />
                ) : (
                  <SquareIcon data-icon="inline-start" />
                )}
                Stoppen
              </Button>
              <Button
                disabled={bezig}
                onClick={() => {
                  if (gepauzeerd) sessie?.hervat();
                  else sessie?.pauzeer();
                  setGepauzeerd(!gepauzeerd);
                }}
                size="sm"
                variant="ghost"
              >
                {gepauzeerd ? (
                  <PlayIcon data-icon="inline-start" />
                ) : (
                  <PauseIcon data-icon="inline-start" />
                )}
                {gepauzeerd ? 'Hervatten' : 'Pauzeren'}
              </Button>
            </div>
          </>
        ) : (
          <Button
            aria-label="Opname starten"
            className="size-16 rounded-full"
            disabled={bezig || !kanOpnemen()}
            onClick={() => void begin()}
            size="icon-lg"
          >
            {bezig ? <Spinner className="size-6" /> : <MicIcon className="size-6" />}
          </Button>
        )}
      </div>
    </div>
  );
}
